import { useMemo } from "react";
import { multiLevelAggregator } from "../../utils/tools";
import { stringMixin } from "../../utils/strings";
import useColors from "../../hooks/useColors";
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";

const formatLevelItems = ({ aggregatedSectors, aggregatedBondCategories, mergedTickerData }) => {
  if (!aggregatedSectors || !aggregatedBondCategories || !mergedTickerData) return [];
  return [
    {
      label: "Equities",
      values: aggregatedSectors.map((x) => ({ ...x, v: `${x.v.toFixed(1)}%` })),
    },
    {
      label: "Fixed Income",
      values: aggregatedBondCategories.map((x) => ({ ...x, v: `${x.v.toFixed(1)}%` })),
    },
    {
      label: "Portfolio Tickers",
      values: mergedTickerData
        .map((x) => ({ k: x.name, v: x.wgt * 100 }))
        .sort((a, b) => b.v - a.v)
        .map((x) => ({ ...x, v: `${x.v.toFixed(2)}%` })),
    },
  ];
};

function aggregatePortfolioData(sortedPortfolios, property) {
  if (!Array.isArray(sortedPortfolios) || sortedPortfolios.length === 0) {
    return null;
  }
  return multiLevelAggregator(
    sortedPortfolios.map((x) => ({
      weight: x.weight,
      values: x?.composite?.[property]
        ?.map((item) => ({
          k: stringMixin.fmt(item.k),
          v: item.v * 100,
        }))
        .sort((a, b) => b.v - a.v),
    }))
  );
}

const usePathHelpers = (portfolios) => {
  //
  // 1) Sort portfolios once, stably
  //
  const sortedPortfolios = useMemo(() => {
    if (!portfolios) return [];
    // Copy, then sort in descending order by weight
    return [...portfolios].sort((a, b) => (b.weight || 0) - (a.weight || 0));
  }, [portfolios]);

  //
  // 2) Memoize each aggregator call
  //
  const aggregatedSectors = useMemo(() => {
    return aggregatePortfolioData(sortedPortfolios, "sector_gic");
  }, [sortedPortfolios]);

  const aggregatedBondCategories = useMemo(() => {
    return aggregatePortfolioData(sortedPortfolios, "bond_categories");
  }, [sortedPortfolios]);

  const aggregatedCategories = useMemo(() => {
    return aggregatePortfolioData(sortedPortfolios, "category");
  }, [sortedPortfolios]);

  //
  // 3) Memoize color usage if it depends on aggregatedCategories
  //
  const categoryColors = useColors({
    numberOfShades: aggregatedCategories?.length || 10,
  });

  //
  // 4) combinedTickers aggregator (also memoized)
  //
  const combinedTickers = useMemo(() => {
    if (!Array.isArray(sortedPortfolios) || sortedPortfolios.length === 0) return null;
    return multiLevelAggregator(
      sortedPortfolios.map((portfolio) => ({
        weight: portfolio.weight,
        values: Object.entries(portfolio.symbols).map(([k, v]) => ({ k, v })),
      }))
    );
  }, [sortedPortfolios]);

  //
  // 5) useTickerSearchIndex - we pass the stable array of ticker symbols
  //
  const tickerSymbols = useMemo(() => {
    if (!combinedTickers) return [];
    return combinedTickers.map((x) => x.k);
  }, [combinedTickers]);

  const { tickers } = useTickerSearchIndex(tickerSymbols);

  //
  // 6) Merge ticker data with aggregator results
  //
  const mergedTickerData = useMemo(() => {
    if (!combinedTickers || !tickers) return [];
    return combinedTickers.map((x) => {
      const found = tickers.find((t) => t.symbol === x.k);
      return {
        wgt: x.v,
        ...found,
      };
    });
  }, [combinedTickers, tickers]);

  //
  // 7) Format final levelItems
  //
  const levelItems = useMemo(() => {
    return formatLevelItems({
      aggregatedSectors,
      aggregatedBondCategories,
      mergedTickerData,
    });
  }, [aggregatedSectors, aggregatedBondCategories, mergedTickerData]);

  //
  // Return everything in a stable object or memo object
  //
  return useMemo(
    () => ({
      sortedPortfolios,
      aggregatedSectors,
      aggregatedBondCategories,
      aggregatedCategories,
      categoryColors,
      mergedTickerData,
      levelItems,
    }),
    [
      sortedPortfolios,
      aggregatedSectors,
      aggregatedBondCategories,
      aggregatedCategories,
      categoryColors,
      mergedTickerData,
      levelItems,
    ]
  );
};

export default usePathHelpers;
